import * as React from 'react';
import { Columns as ColumnsIcon, CaretDown, Lock } from '../../../design-system/icons';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from '../../ui/dropdown-menu';
import type { ColumnMenuItem } from './useColumnVisibility';

/** Trigger button — sized/bordered to line up with the search field beside it. */
const TRIGGER_CLASS = 'h-10 gap-2 rounded-lg border-border bg-card';
/** Compact hidden-count badge on the trigger. */
const BADGE_CLASS = 'h-4 min-w-4 px-1 text-[10px] font-semibold';
/** Section heading ("Always visible" / "Show columns"). */
const SECTION_LABEL_CLASS = 'text-[10px] tracking-wide text-muted-foreground uppercase';
/** Reset action — a brand-coloured menu item (Radix handles its disabled look). */
const RESET_CLASS = 'ml-auto w-auto text-caption-medium text-(--brand-500)';
/** Always-visible row: locked (disabled) but not dimmed — a light background and
 *  full-opacity text, overriding the menu item's default disabled fade. */
const ALWAYS_VISIBLE_ROW_CLASS = 'gap-3 bg-muted/60 data-disabled:opacity-100';

interface ColumnsMenuProps {
  items: ColumnMenuItem[];
  /** Number of hidden columns — shown as a badge on the trigger when > 0. */
  hiddenCount: number;
  onToggle: (id: string) => void;
  onReset: () => void;
}

/** One column row in the menu: a labelled checkbox (+ a lock for always-visible
 *  columns). Rendered as a Radix checkbox item so assistive tech gets
 *  `role="menuitemcheckbox"` + `aria-checked` (its built-in check indicator
 *  shows the state); `onSelect` is prevented so the menu stays open while
 *  several columns are toggled, and always-visible rows are disabled so they
 *  can't be turned off. The design-system Checkbox is presentational only —
 *  `disabled` makes it inert and non-focusable at the Radix primitive (so
 *  `aria-hidden` on it is spec-valid), and the opacity override keeps the box
 *  looking normal; the CheckboxItem owns the interaction and `aria-checked`. */
function ColumnRow({ item, onToggle }: { item: ColumnMenuItem; onToggle: (id: string) => void }) {
  const rowClassName = item.alwaysVisible ? ALWAYS_VISIBLE_ROW_CLASS : 'gap-3';
  return (
    <DropdownMenuCheckboxItem
      className={rowClassName}
      checked={item.visible}
      disabled={item.alwaysVisible}
      showIndicator={false}
      onCheckedChange={() => onToggle(item.id)}
      onSelect={(event) => event.preventDefault()}
    >
      <Checkbox checked={item.visible} disabled aria-hidden className="pointer-events-none disabled:opacity-100" />
      <span className="flex-1">{item.label}</span>
      {item.alwaysVisible && <Lock size={14} className="text-muted-foreground" />}
    </DropdownMenuCheckboxItem>
  );
}

/**
 * Columns visibility dropdown for a DataTable. Renders a locked "Always
 * visible" section and a toggleable "Show columns" section, plus a Reset
 * action that restores every column. Drives the model returned by
 * {@link useColumnVisibility}.
 */
export function ColumnsMenu({ items, hiddenCount, onToggle, onReset }: ColumnsMenuProps): React.ReactElement {
  // Reset without closing the menu, so the effect is visible in place.
  const handleResetSelect = (event: Event) => {
    event.preventDefault();
    onReset();
  };
  const alwaysVisible = items.filter((item) => item.alwaysVisible);
  const toggleable = items.filter((item) => !item.alwaysVisible);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={TRIGGER_CLASS}>
          <ColumnsIcon size={16} className="text-foreground" />
          Columns
          {hiddenCount > 0 && (
            <Badge variant="secondary" className={BADGE_CLASS}>
              {hiddenCount}
            </Badge>
          )}
          <CaretDown size={14} className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-body-sm-semibold text-foreground">Columns</span>
          <DropdownMenuItem disabled={hiddenCount === 0} onSelect={handleResetSelect} className={RESET_CLASS}>
            Reset
          </DropdownMenuItem>
        </div>

        {alwaysVisible.length > 0 && (
          <>
            <DropdownMenuLabel className={SECTION_LABEL_CLASS}>Always visible</DropdownMenuLabel>
            {alwaysVisible.map((item) => (
              <ColumnRow key={item.id} item={item} onToggle={onToggle} />
            ))}
          </>
        )}

        {toggleable.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className={SECTION_LABEL_CLASS}>Show columns</DropdownMenuLabel>
            {toggleable.map((item) => (
              <ColumnRow key={item.id} item={item} onToggle={onToggle} />
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
